import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { useAiRunStore } from '../store/aiRunStore';
import api from '../services/api';
import { socketService } from '../services/socket';
import type { ApiResponse, AuthTokens, UserRole } from '../types/index';

export interface LoginResult {
  success: boolean;
  needsVerification?: boolean;
  comingSoon?: boolean;
  email?: string;
}

export interface RegisterInput {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  fieldOfWork?: string;
}

export interface RegisterResult {
  success: boolean;
  email?: string;
  comingSoon?: boolean;
}

export function useAuth() {
  const { user, accessToken, isAuthenticated, setAuth, logout: storeLogout } = useAuthStore();
  const clearActiveDetectorJob = useAiRunStore((s) => s.clearActiveDetectorJob);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSuperAdmin = (user?.prms ?? []).includes('*');

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await api.post<ApiResponse<AuthTokens>>('/auth/login', { email, password });
        const data = res.data.data;
        if (data) {
          setAuth(data.user, data.accessToken);
        }
        return { success: true };
      } catch (err: unknown) {
        const resData = (err as { response?: { data?: { message?: string; code?: string; data?: { emailVerificationEnabled?: boolean } } } }).response?.data;
        // Unverified account — only redirect to /verify when email verification
        // is actually enabled; otherwise there's no code to deliver, show "coming soon".
        if (resData?.code === 'EMAIL_NOT_VERIFIED') {
          if (!resData?.data?.emailVerificationEnabled) {
            return { success: false, comingSoon: true, email };
          }
          return { success: false, needsVerification: true, email };
        }
        setError(resData?.message ?? 'Login failed. Please try again.');
        return { success: false };
      } finally {
        setIsLoading(false);
      }
    },
    [setAuth]
  );

  const register = useCallback(
    async (input: RegisterInput): Promise<RegisterResult> => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await api.post<ApiResponse<{ email: string; requiresVerification: boolean; emailVerificationEnabled?: boolean }>>('/auth/register', input);
        return {
          success: true,
          email: res.data.data?.email ?? input.email,
          comingSoon: !res.data.data?.emailVerificationEnabled,
        };
      } catch (err: unknown) {
        const resData = (err as { response?: { data?: { message?: string; errors?: { field?: string; message: string }[] } } }).response?.data;
        setError(resData?.errors?.[0]?.message ?? resData?.message ?? 'Registration failed. Please try again.');
        return { success: false };
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const verifyEmail = useCallback(
    async (email: string, code: string): Promise<boolean> => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await api.post<ApiResponse<AuthTokens>>('/auth/verify-email', { email, code });
        const data = res.data.data;
        if (data) {
          setAuth(data.user, data.accessToken);
        }
        return true;
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
          'Verification failed. Please try again.';
        setError(message);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [setAuth]
  );

  const resendVerification = useCallback(
    async (email: string): Promise<{ success: boolean; message: string }> => {
      try {
        const res = await api.post<ApiResponse<null>>('/auth/resend-verification', { email });
        return { success: true, message: res.data.message ?? 'A new code has been sent.' };
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
          'Could not resend the code. Please try again.';
        return { success: false, message };
      }
    },
    []
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Always clear local state even if the API call fails
    } finally {
      socketService.disconnect();
      // Clear any in-flight detector job tracking too, otherwise the global
      // poller (which runs regardless of route) keeps hitting the job-status
      // endpoint with a now-cleared token, forcing repeated refresh failures.
      clearActiveDetectorJob();
      storeLogout();
      queryClient.clear();
      navigate('/login', { replace: true });
    }
  }, [storeLogout, clearActiveDetectorJob, queryClient, navigate]);

  const clearError = useCallback((): void => setError(null), []);

  const hasRole = (...roles: UserRole[]): boolean => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  return {
    user,
    accessToken,
    isAuthenticated,
    isLoading,
    error,
    login,
    register,
    verifyEmail,
    resendVerification,
    logout,
    clearError,
    hasRole,
    isSuperAdmin,
  };
}
